import React, { useState, useEffect, useRef, Component, ErrorInfo, ReactNode } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { useLocation } from 'react-router-dom';
import AppRoutes from './routes/AppRoutes';
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import LoginModal from './components/LoginModal';
import { reapplyGoogleTranslate } from './hooks/useGoogleTranslate';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

// ---------------------------------------------------------------------------
// Top-level Error Boundary — catches any uncaught React errors (including
// Google Translate DOM-mutation conflicts) and lets the user recover.
// ---------------------------------------------------------------------------
interface AppErrorBoundaryState { hasError: boolean; error: Error | null }
class AppErrorBoundary extends Component<{ children: ReactNode }, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary]', error.message, info.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: '#fafaf8', flexDirection: 'column', p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Something went wrong
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mb: 3, maxWidth: 400 }}>
            An unexpected error occurred. Please try again.
          </Typography>
          <Button
            variant="contained"
            onClick={this.handleReload}
            sx={{ bgcolor: '#0d9488', textTransform: 'none', fontWeight: 600, borderRadius: '12px', px: 4, '&:hover': { bgcolor: '#0f766e' } }}
          >
            Try Again
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

function App() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/login';
  const isPromotionRoute = location.pathname.startsWith('/promotions/');
  const isHomeRoute = location.pathname === '/';
  const [isLoading, setIsLoading] = useState(true);
  // Counter that increments on every route change to re-trigger the CSS animation
  const [transitionKey, setTransitionKey] = useState(0);
  const prevPathRef = useRef(location.pathname);

  // Initial page load — show loading screen briefly, exit as soon as ready
  useEffect(() => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setIsLoading(false);
    };

    // Minimum display time so the screen doesn't flash
    const minTimer = setTimeout(finish, 800);
    // Hard cap so it never stalls longer than 1.5s
    const maxTimer = setTimeout(finish, 1500);

    // Exit as soon as the page's critical resources have loaded
    if (document.readyState === 'complete') {
      setTimeout(finish, 600);
    } else {
      window.addEventListener('load', () => setTimeout(finish, 200), { once: true });
    }

    return () => {
      clearTimeout(minTimer);
      clearTimeout(maxTimer);
    };
  }, []);

  // On every route change: play a smooth content transition and reapply translation
  useEffect(() => {
    if (isLoading) return;
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;

    // Bump key to restart the CSS fade-slide animation on the content wrapper
    setTransitionKey((k) => k + 1);

    // Send pageview to Google Analytics on every route change
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }

    // Reapply Google Translate for new DOM (runs in background, no hiding)
    requestAnimationFrame(() => {
      reapplyGoogleTranslate();
    });
  }, [location.pathname, location.search, isLoading]);

  if (isLoading && !isPromotionRoute) {
    return <LoadingScreen />;
  }

  return (
    <AppErrorBoundary>
    <AuthProvider>
    <LanguageProvider>
      <CartProvider>
        {!isAdminRoute && !isPromotionRoute && <Header />}
        <Box
          key={transitionKey}
          sx={{
            animation: 'pageFadeSlideIn 0.35s ease-out both',
            '@keyframes pageFadeSlideIn': {
              '0%': { opacity: 0.3, transform: 'translateY(10px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
            minHeight: '100vh',
          }}
        >
          {isAdminRoute ? (
            <Container maxWidth="xl" sx={{ mt: 0, mb: 8, px: { xs: 0, sm: 0, lg: 0 } }}>
              <AppRoutes />
            </Container>
          ) : isHomeRoute || isPromotionRoute ? (
            <Box sx={{ mt: 0, mb: isPromotionRoute ? 0 : 8, width: '100%' }}>
              <AppRoutes />
            </Box>
          ) : (
            <Container maxWidth="lg" sx={{ mt: 0, mb: 8, px: { xs: 0, sm: 0, lg: 0 } }}>
              <AppRoutes />
            </Container>
          )}
          {!isAdminRoute && !isPromotionRoute && <Footer />}
        </Box>
        <LoginModal />
      </CartProvider>
    </LanguageProvider>
    </AuthProvider>
    </AppErrorBoundary>
  );
}

export default App;
